import { useMutation } from '@tanstack/react-query';

import type { LSGCreateReportSettings } from '@brightlocal/api-client';
import { LSG_GRID_SIZES, REPORT_SCHEDULE } from '@brightlocal/shared';

import { apiClient } from 'shared/apiClient';
import type { ErrorReporting, Feature } from 'shared/utils';

import { REPORT_CREATE_STEPS, REPORT_CREATE_TOOLS } from '../consts';
import { buildReportCreateErrorReport, ReportCreateError, runReportStep } from '../utils';

const MAX_LSG_KEYWORDS = 5;
const DEFAULT_LSG_GRID_SIZE = LSG_GRID_SIZES.SEVEN_BY_SEVEN;
const DEFAULT_LSG_GRID_POINT_SPACING = '1mi';
const DEFAULT_LSG_SCHEDULE = REPORT_SCHEDULE.ADHOC;

const TOOL = REPORT_CREATE_TOOLS.LSG;
const METHOD = 'apiClient.lsg.createReport / apiClient.lsg.runReport';

interface UseLSGReportMutationOptions {
  feature: Feature;
}

export const useLSGReportMutation = ({ feature }: UseLSGReportMutationOptions) =>
  useMutation({
    mutationFn: async ({
      locationId,
      keywords,
      country
    }: {
      locationId: number;
      keywords: string[];
      country?: string;
    }) => {
      const settings: LSGCreateReportSettings = {
        locationId,
        keywords: keywords.slice(0, MAX_LSG_KEYWORDS),
        gridSize: DEFAULT_LSG_GRID_SIZE,
        gridPointSpacing: DEFAULT_LSG_GRID_POINT_SPACING,
        schedule: DEFAULT_LSG_SCHEDULE
      };
      const context = { locationId, country };

      const createdReport = await runReportStep({
        tool: TOOL,
        step: REPORT_CREATE_STEPS.CREATE,
        context,
        request: () => apiClient.lsg.createReport(settings)
      });
      const { reportId } = createdReport;

      if (!reportId) {
        // TODO: Added specific handling if agreed [https://brightlocal.atlassian.net/browse/NP-89]
        throw new ReportCreateError({
          tool: TOOL,
          step: REPORT_CREATE_STEPS.CREATE,
          reason: 'missingReportId',
          context,
          cause: new Error(`Creation of LSG report failed. Response: ${JSON.stringify(createdReport)}`)
        });
      }

      await runReportStep({
        tool: TOOL,
        step: REPORT_CREATE_STEPS.RUN,
        context,
        request: () => apiClient.lsg.runReport(reportId)
      });
    },
    meta: {
      errorReporting: {
        feature,
        errorMessage: 'Error creating LSG report',
        method: METHOD,
        resolve: (error) => buildReportCreateErrorReport({ tool: TOOL, feature, method: METHOD, error })
      } satisfies ErrorReporting
    }
  });
